import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button, Checkbox, Pagination, Table, TableColumn } from 'common/components';
import { Modal } from 'common/components/Modal';
import { useTenantScope } from 'hooks/useTenantScope';
import { useGetWorkflowPhoneNumbers } from 'services/hooks/queries';
import { useDeleteWorkflowPhoneNumbers } from 'services/hooks/mutations';
import {
    CommunicationMedium,
    PhoneNumberLifecycleStatus,
    WorkflowPhoneNumber,
} from 'services/api/getWorkflowPhoneNumbers';
import { DeleteWorkflowPhoneNumbersResult } from 'services/api/deleteWorkflowPhoneNumbers';
import bulkActionsStyles from '../components/BulkActions/BulkActions.module.scss';

import styles from '../AgenticWorkflowDetail.module.scss';

const COMMUNICATION_MEDIUM_LABELS: Record<CommunicationMedium, string> = {
    CALL: 'Call',
    SMS: 'SMS',
    BOTH: 'Call & SMS',
};

const LIFECYCLE_STATUS_LABELS: Record<PhoneNumberLifecycleStatus, string> = {
    PURCHASED: 'Purchased',
    REGISTERING: 'Registering',
    PENDING_PROPAGATION: 'Pending propagation',
    WARMUP_T1: 'Warming up (tier 1)',
    WARMUP_T2: 'Warming up (tier 2)',
    WARMUP_T3: 'Warming up (tier 3)',
    WARMUP_T4: 'Warming up (tier 4)',
    WARMUP_T5: 'Warming up (tier 5)',
    ACTIVE: 'Active',
    QUARANTINED: 'Quarantined',
    RELEASED: 'Released',
};

const WARMUP_SKIPPED_LABEL = 'Active (warm-up skipped)';

const formatLifecycleStatus = (status?: string): string =>
    status ? (LIFECYCLE_STATUS_LABELS[status as PhoneNumberLifecycleStatus] ?? status) : '—';

const formatProvider = (provider: string): string =>
    provider ? provider.charAt(0).toUpperCase() + provider.slice(1) : '—';

const formatCommunicationMedium = (medium: CommunicationMedium | string): string =>
    COMMUNICATION_MEDIUM_LABELS[medium as CommunicationMedium] ?? medium ?? '—';

const pluralizeNumbers = (count: number): string => `${count} phone number${count === 1 ? '' : 's'}`;

const getDeleteErrorMessage = (error: unknown): string => {
    const err = error as { response?: { data?: { error?: string; message?: string } }; message?: string };
    return (
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to delete phone numbers. Please try again.'
    );
};

const showDeleteResultToasts = (result: DeleteWorkflowPhoneNumbersResult) => {
    const alsoDeletedRows = result.alsoDeletedRows ?? [];
    const failedNumbers = result.failedToReleaseNumbers ?? [];
    // softDeletedRows counts the selected rows plus sibling rows on other workflows.
    const deletedCount = Math.max((result.softDeletedRows ?? 0) - alsoDeletedRows.length, 0);

    if (deletedCount > 0) {
        let message = `Deleted ${pluralizeNumbers(deletedCount)}.`;
        if (alsoDeletedRows.length > 0) {
            const details = alsoDeletedRows
                .map((row) => `${row.phoneNumber} (${row.workflowId ? `workflow ${row.workflowId}` : 'no workflow'})`)
                .join(', ');
            message += ` Also removed from other workflows: ${details}.`;
        }
        toast.success(message);
    }

    const warningParts: string[] = [];
    if (failedNumbers.length > 0) {
        warningParts.push(`Failed to release from the carrier: ${failedNumbers.join(', ')}.`);
    }
    if (result.warnings) {
        warningParts.push(result.warnings);
    }
    if (warningParts.length > 0) {
        toast.warning(warningParts.join(' '));
    }
};

const WorkflowPhoneNumbersPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [pendingDeleteIds, setPendingDeleteIds] = useState<string[] | null>(null);

    const { isSuperAdmin } = useTenantScope();
    const { deleteWorkflowPhoneNumbers, isDeletingWorkflowPhoneNumbers } = useDeleteWorkflowPhoneNumbers();

    const { phoneNumbers, isPhoneNumbersFetching } = useGetWorkflowPhoneNumbers({
        workflowId: id || '',
        currentPage,
        perPage: rowsPerPage,
    });

    const rows = useMemo(() => phoneNumbers?.data ?? [], [phoneNumbers?.data]);
    const totalPages = phoneNumbers?.pagination?.totalPages || 1;
    const currentPageFromAPI = phoneNumbers?.pagination?.currentPage || currentPage;
    const perPageFromAPI = phoneNumbers?.pagination?.perPage || rowsPerPage;

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    // Selection is scoped to the visible page.
    useEffect(() => {
        setSelectedIds([]);
    }, [currentPage, rowsPerPage]);

    const pageIds = useMemo(() => rows.map((row) => row.id), [rows]);
    const areAllSelected = pageIds.length > 0 && pageIds.every((rowId) => selectedIds.includes(rowId));
    const areSomeSelected = !areAllSelected && pageIds.some((rowId) => selectedIds.includes(rowId));

    const columns: TableColumn<WorkflowPhoneNumber>[] = useMemo(() => {
        const baseColumns: TableColumn<WorkflowPhoneNumber>[] = [
            {
                key: 'phoneNumber',
                title: 'Phone Number',
                dataIndex: 'phoneNumber',
                width: 180,
            },
            {
                key: 'provider',
                title: 'Provider',
                width: 140,
                render: (_, record) => formatProvider(record.provider),
            },
            {
                key: 'outboundTrunkSid',
                title: 'Outbound Trunk SID',
                dataIndex: 'outboundTrunkSid',
                width: 220,
            },
            {
                key: 'communicationMedium',
                title: 'Communication Medium',
                width: 180,
                render: (_, record) => formatCommunicationMedium(record.communicationMedium),
            },
            {
                key: 'lifecycleStatus',
                title: 'Status',
                width: 200,
                render: (_, record) =>
                    record.warmupSkipped ? (
                        <span title={formatLifecycleStatus(record.lifecycleStatus)}>{WARMUP_SKIPPED_LABEL}</span>
                    ) : (
                        formatLifecycleStatus(record.lifecycleStatus)
                    ),
            },
            {
                key: 'dailyCallCap',
                title: 'Daily call cap',
                width: 140,
                render: (_, record) =>
                    record.dialable && typeof record.dailyCallCap === 'number' ? record.dailyCallCap : '—',
            },
        ];

        if (!isSuperAdmin) {
            return baseColumns;
        }

        const toggleRow = (rowId: string) =>
            setSelectedIds((prev) =>
                prev.includes(rowId) ? prev.filter((selectedId) => selectedId !== rowId) : [...prev, rowId]
            );

        return [
            {
                key: 'checkbox',
                title: (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                            checked={areAllSelected}
                            indeterminate={areSomeSelected}
                            onChange={() => setSelectedIds(areAllSelected ? [] : pageIds)}
                            disabled={pageIds.length === 0}
                            onClick={(e) => e.stopPropagation()}
                            aria-label='Select all phone numbers on this page'
                        />
                    </div>
                ),
                width: 40,
                align: 'center',
                render: (_, record) => (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                            checked={selectedIds.includes(record.id)}
                            onChange={() => toggleRow(record.id)}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Select ${record.phoneNumber}`}
                        />
                    </div>
                ),
            },
            ...baseColumns,
            {
                key: 'actions',
                title: 'Actions',
                width: 80,
                render: (_, record) => (
                    <Button
                        variant='outline-secondary'
                        size='sm'
                        icon={Trash2}
                        iconOnly
                        onClick={() => setPendingDeleteIds([record.id])}
                        title='Delete phone number'
                    />
                ),
            },
        ];
    }, [isSuperAdmin, areAllSelected, areSomeSelected, pageIds, selectedIds]);

    const closeConfirm = () => {
        if (isDeletingWorkflowPhoneNumbers) return;
        setPendingDeleteIds(null);
    };

    const handleConfirmDelete = async () => {
        if (!id || !pendingDeleteIds?.length) return;
        try {
            const result = await deleteWorkflowPhoneNumbers({ workflowId: id, phoneNumberIds: pendingDeleteIds });
            showDeleteResultToasts(result);
            setSelectedIds([]);
            setPendingDeleteIds(null);
        } catch (error) {
            toast.error(getDeleteErrorMessage(error));
        }
    };

    const pendingCount = pendingDeleteIds?.length ?? 0;
    const pendingLabel =
        pendingCount === 1
            ? rows.find((row) => row.id === pendingDeleteIds?.[0])?.phoneNumber || 'this phone number'
            : `${pendingCount} phone numbers`;

    return (
        <div className='flex flex-col gap-2xl' data-testid='workflow-phone-numbers-page'>
            <Table<WorkflowPhoneNumber>
                data={rows}
                columns={columns}
                emptyText='No phone numbers assigned to this workflow.'
                rowKey={(record) => record.id}
                showHeader
                variant='secondary'
                hoverable
                size='medium'
                isLoading={isPhoneNumbersFetching}
            />
            <div className={`flex ${isSuperAdmin ? 'justify-between' : 'justify-end'} items-center flex-wrap gap-sm`}>
                {isSuperAdmin && (
                    <div>
                        {selectedIds.length > 0 && (
                            <div
                                className={bulkActionsStyles.workflowLeadsActions}
                                data-testid='phone-numbers-bulk-actions'
                            >
                                <span>Selected {selectedIds.length}</span>
                                <div className={bulkActionsStyles.buttonsContainer}>
                                    <Button
                                        variant='primary'
                                        size='sm'
                                        icon={Trash2}
                                        iconOnly
                                        onClick={() => setPendingDeleteIds(selectedIds)}
                                        title='Delete selected phone numbers'
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}
                <Pagination
                    currentPage={currentPageFromAPI}
                    totalPages={totalPages}
                    rowsPerPage={perPageFromAPI}
                    onPageChange={setCurrentPage}
                    onRowsPerPageChange={(newRowsPerPage) => {
                        setRowsPerPage(newRowsPerPage);
                        setCurrentPage(1);
                    }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    className={styles.agenticPagination}
                />
            </div>
            {isSuperAdmin && (
                <Modal
                    isOpen={pendingCount > 0}
                    onClose={closeConfirm}
                    title={pendingCount === 1 ? 'Delete phone number' : 'Delete phone numbers'}
                    size='sm'
                    showDefaultFooter={false}
                    closeOnOverlayClick={!isDeletingWorkflowPhoneNumbers}
                    closeOnEscape={!isDeletingWorkflowPhoneNumbers}
                    footer={
                        <div className='flex justify-end gap-sm'>
                            <Button
                                variant='outline-secondary'
                                onClick={closeConfirm}
                                disabled={isDeletingWorkflowPhoneNumbers}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant='primary'
                                icon={Trash2}
                                onClick={handleConfirmDelete}
                                isLoading={isDeletingWorkflowPhoneNumbers}
                                disabled={isDeletingWorkflowPhoneNumbers}
                                data-testid='confirm-delete-phone-numbers'
                            >
                                Delete
                            </Button>
                        </div>
                    }
                >
                    <p data-testid='delete-phone-numbers-confirm-message'>
                        This permanently releases {pendingLabel} from the carrier (Telnyx or Twilio).{' '}
                        {pendingCount === 1 ? 'It is' : 'They are'} also removed from any other workflow using the same
                        number, and any calls in progress on {pendingCount === 1 ? 'it' : 'them'} will drop. This cannot
                        be undone.
                    </p>
                </Modal>
            )}
        </div>
    );
};

export default WorkflowPhoneNumbersPage;
